// Pure helpers for My Contacts sync semantics. Extracted so the benign-skip
// classification, toast severity, and the auto-sync-once-per-login gate are
// unit-testable without a DOM. See docs/CONTACT-SYNC.md.

// A refresh outcome is one of:
//  - 'synced'  : a live profile was re-pulled successfully.
//  - 'skipped' : a benign non-active profile (owner DISABLED it) — returns 200
//                with frozen fields; NEVER counts as a failure.
//  - 'revoked' : the owner revoked the subscription — returns 200 with frozen
//                fields; benign (grayed) but tracked separately for messaging.
//  - 'failed'  : a REAL error (network / 500). The ONLY warn-worthy outcome.
export type SyncOutcome = 'synced' | 'skipped' | 'revoked' | 'failed';

// classifyRefreshError maps a thrown refresh error to a NON-failed outcome when
// it is a benign disabled/revoked signal (stale client that still got an error
// instead of the 200 path), else 'failed'. Disabled/revoked are NEVER 'failed'.
export function classifyRefreshError(message: string | undefined): SyncOutcome {
  const msg = message || '';
  if (/revoked|no longer shar|stopped shar/i.test(msg)) return 'revoked';
  if (/disabled/i.test(msg)) return 'skipped';
  return 'failed';
}

// classifyRefreshState maps a 200-response sync_state to an outcome. A disabled
// profile now returns 200 sync_state='profile_disabled' (benign skip); a revoked
// one returns 200 sync_state='revoked'. Everything else counts as synced.
export function classifyRefreshState(syncState: string | undefined): SyncOutcome {
  if (syncState === 'profile_disabled') return 'skipped';
  if (syncState === 'revoked') return 'revoked';
  return 'synced';
}

export interface SyncTally {
  synced: number;
  skipped: number;
  revoked: number;
  failed: number;
}

export function emptyTally(): SyncTally {
  return { synced: 0, skipped: 0, revoked: 0, failed: 0 };
}

// syncToastSeverity: warn ONLY when a REAL failure occurred. A partial run where
// the only non-synced profiles are disabled/revoked is informational ('ok'),
// never 'warn' — disabling/revoking a profile must not surface an error toast.
export function syncToastSeverity(t: SyncTally): 'ok' | 'warn' {
  return t.failed > 0 ? 'warn' : 'ok';
}

// --- Auto "Sync all" once per login session gate ---

export const AUTOSYNC_FLAG = 'vid.contacts.autosynced';

// shouldAutoSync returns true iff the auto-sync-all has NOT yet fired this login
// session (flag unset). Callers mark it fired via markAutoSynced() right after.
export function shouldAutoSync(storage: Pick<Storage, 'getItem'>): boolean {
  return storage.getItem(AUTOSYNC_FLAG) !== '1';
}

export function markAutoSynced(storage: Pick<Storage, 'setItem'>): void {
  storage.setItem(AUTOSYNC_FLAG, '1');
}

// resetAutoSync clears the gate so a fresh LOGIN (or logout) re-enables the
// one-shot auto-sync. Called from session.set()/clear().
export function resetAutoSync(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(AUTOSYNC_FLAG);
}

// --- Per-CARD sync semantics ---
//
// A "contact card" is the union of every subscription I hold to the same person
// (grouped by owner_id; see merge.ts / docs/CONTACT-SYNC.md). Sync is COUNTED and
// COMMUNICATED per CARD, never per underlying profile. The helpers below are the
// pure, DOM-free source of truth for the counter label and the two toasts.

// cardSyncLabel is the progress label shown while syncing all cards: 1-based
// index over the number of CARDS (e.g. 2 cards where one has 2 profiles shows
// "Syncing 1/2…", "Syncing 2/2…" — never 3).
export function cardSyncLabel(index1: number, numCards: number): string {
  return `Syncing ${index1}/${numCards}…`;
}

// CardSyncTally counts per CARD: a card is `synced` if it did not hard-fail
// (even if some of its profiles were benign skips), `failed` only on a REAL
// error (network/500) across the card.
export interface CardSyncTally {
  synced: number;
  failed: number;
}

export function emptyCardTally(): CardSyncTally {
  return { synced: 0, failed: 0 };
}

// syncAllToast builds the final Sync-all toast, phrased PER CARD. Success is
// simply `Synced {N}`; a real per-card failure appends a warn. Benign
// skipped/revoked PROFILE counts are NEVER surfaced here.
export function syncAllToast(t: CardSyncTally): { text: string; severity: 'ok' | 'warn' } {
  const parts = [`Synced ${t.synced}`];
  if (t.failed) parts.push(`${t.failed} contact${t.failed > 1 ? 's' : ''} failed to sync`);
  return { text: parts.join(' · '), severity: t.failed ? 'warn' : 'ok' };
}

// quietSyncToast is the auto/background path: silent unless a REAL per-card
// failure occurred, phrased per card. Returns null when nothing to say.
export function quietSyncToast(t: CardSyncTally): { text: string; severity: 'warn' } | null {
  if (!t.failed) return null;
  return { text: `${t.failed} contact${t.failed > 1 ? 's' : ''} failed to sync`, severity: 'warn' };
}

// CardRefreshCounts summarizes one card's per-profile refresh outcomes.
export interface CardRefreshCounts {
  // Number of the card's profiles that refreshed live (synced).
  synced: number;
  // Benign non-active profiles (owner revoked/disabled) encountered this refresh.
  benign: number;
  // REAL failures (network/500).
  failed: number;
  // Total profiles attempted for this card.
  total: number;
}

// cardRefreshToast derives the SINGLE-card Refresh toast from that card's
// outcomes. Rules (see docs/CONTACT-SYNC.md):
//   - Any REAL failure across ALL of the card's profiles → warn "Some details
//     failed to refresh" (phrased per card, no profile churn).
//   - The WHOLE contact stopped sharing (nothing left syncing: every profile is
//     benign/failed and none synced, driven by allGone) → informative message.
//   - Otherwise → exactly "Refreshed" (ok). A PARTIAL revoke/disable where the
//     person still shares ≥1 profile reads plainly "Refreshed" — NO mention of
//     the dropped profile.
//
// `allGone` is computed by the caller from the just-mutated subscriptions (every
// profile of this person is now revoked/disabled — the card has nothing left).
export function cardRefreshToast(
  c: CardRefreshCounts,
  allGone: boolean,
): { text: string; severity: 'ok' | 'warn' } {
  if (c.failed > 0 && c.synced === 0 && c.benign === 0) {
    return { text: 'Some details failed to refresh', severity: 'warn' };
  }
  if (allGone) {
    return { text: 'This contact stopped sharing with you', severity: 'warn' };
  }
  if (c.failed > 0) {
    return { text: 'Some details failed to refresh', severity: 'warn' };
  }
  return { text: 'Refreshed', severity: 'ok' };
}
